import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { saveAs } from 'file-saver';
import { JobsService, StoreService } from 'core/index';
import { KeplerProgressListService } from './kepler-progress-list.service';
import { KeplerService } from 'app/kepler/services/kepler.service';
import { ListHelperService } from '../../../core/services/list-helper.service';

const LIST_ID = 'KEPLER_PROGRESS_LIST';

@Component({
  selector: 'lms-kepler-progress-list',
  templateUrl: './kepler-progress-list.component.html',
  styleUrls: ['./kepler-progress-list.component.scss'],
  providers: [KeplerProgressListService, KeplerService]
})
export class KeplerProgressListComponent implements OnInit {
  @Input() options: any;
  @Input() showToolbar: boolean = true;

  public didMatchStart: boolean;
  public didMatchEnd: boolean;
  public rangeConfig;
  public maxMilestone;
  public toolbarConfig: any;
  public dataSource: any;
  public playerListId: string;

  private keplerMatch;
  private routeSubscription: Subscription;
  private jobSubscription: Subscription;

  constructor(
    private keplerProgressListService: KeplerProgressListService,
    public route: ActivatedRoute,
    public translate: TranslateService,
    public keplerService: KeplerService,
    public cdr: ChangeDetectorRef,
    public jobsService: JobsService,
    public http: HttpClient,
    public listHelper: ListHelperService
  ) {}

  ngOnInit(): void {
    this.playerListId = LIST_ID + '_' + Date.now();
    this.toolbarConfig = {
      list: {
        listId: this.playerListId,
        download: false,
        filterRow: true,
        columnChooser: false,
        skeletor: true,
        customDownload: true
      }
    };
    this.getRouteData$();
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) this.routeSubscription.unsubscribe();
    if (this.jobSubscription) this.jobSubscription.unsubscribe();
  }

  private getRouteData$() {
    this.routeSubscription = this.route.data.subscribe(async (data) => {
      this.keplerMatch = data['keplerMatch'];
      this.didMatchStart = new Date(this.keplerMatch.startDate) < new Date();
      this.didMatchEnd = new Date(this.keplerMatch.endDate) < new Date();

      this.setRange();
      this.dataSource = await this.keplerProgressListService.getDatasource(
        this.playerListId,
        this.options,
        this.keplerMatch,
        true
      );
    });
  }

  setRange() {
    const sliderMilestones = [...Array(this.keplerMatch.roundCount + 1).keys()].slice(1);
    if (!this.keplerMatch.roundCount || this.keplerMatch.hasLastRoundEnded) {
      // today or match end milestone
      sliderMilestones.push(!this.keplerMatch.roundCount ? 0 : this.keplerMatch.roundCount + 1);
    }
    this.maxMilestone = sliderMilestones[sliderMilestones.length - 1];

    this.rangeConfig = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      hide_from_to: !this.didMatchStart,
      keyboard: true,
      grid: false,
      from: this.maxMilestone,
      values: sliderMilestones,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      extra_classes: this.didMatchStart ? 'margin-top margin-left margin-right' : 'margin-left margin-right',
      prettify: (selectedMilestone: number) => this.prettify(selectedMilestone),
      onFinish: async (event: any) => await this.onFinish(event)
    };
  }

  private prettify(selectedMilestone: number) {
    const todayMilestone = this.keplerMatch.hasLastRoundEnded
      ? this.keplerMatch.roundCount + 1
      : this.keplerMatch.roundCount;

    if (selectedMilestone === todayMilestone) {
      if (this.didMatchEnd) {
        return this.translate.instant('kepler.ranking.endedMatch');
      }
      return this.translate.instant('calendar.today');
    }
    return this.translate.instant('kepler.ranking.uploadedKpi', { num: selectedMilestone });
  }

  private async onFinish(event: any, propagate = true) {
    // event.from still goes from 0 to N even if values go from 1 to N
    const requestedRound = event.from + 1;
    const maxRequestableRound = this.keplerMatch.hasLastRoundEnded
      ? this.keplerMatch.roundCount
      : this.keplerMatch.roundCount - 1;
    const todayMilestone = this.keplerMatch.hasLastRoundEnded
      ? this.keplerMatch.roundCount + 1
      : this.keplerMatch.roundCount;
    this.options.filters.round = requestedRound > maxRequestableRound ? 'final' : requestedRound;

    if (propagate) {
      this.dataSource = await this.keplerProgressListService.getDatasource(
        this.playerListId,
        this.options,
        this.keplerMatch,
        todayMilestone == requestedRound
      );
      this.dataSource.refreshData({ filters: this.options.filters, page: 1 });
    }
  }

  public onDatasourceUpdate(dataSource) {
    this.dataSource = dataSource;
    StoreService.update(this.playerListId, this.dataSource);
  }

  public downloadReport(all?: boolean) {
    this.cdr.detectChanges();
    const { filters, page, limit, sort, search, aggregates, populate } = this.dataSource;
    const { matchId, status, round = 'final', ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString(
      $filterValues,
      this.dataSource,
      this.keplerProgressListService.getCustomFilters()
    );

    const paginationOptions = all ? { export: 'ALL' } : { page, limit };
    this.keplerService
      .downloadRanking(matchId, {
        ...paginationOptions,
        sort,
        aggregates,
        populate,
        search,
        round: round || 'final',
        $filter,
        $status: status
      })
      .subscribe((res) => this.manageBulkJob(res));
  }

  private manageBulkJob(job) {
    this.jobSubscription = this.jobsService.manageJob(job.jobId, 'DOWNLOAD_MATCH_RANKING').subscribe((job) => {
      if (job.status === 'finished' || job.status === 'fatal-error') {
        this.jobSubscription.unsubscribe();
        this.http.get(job.details.summary.fileUrl, { responseType: 'blob' }).subscribe((res) => {
          const { matchId, round } = this.options.filters;
          saveAs(res, round ? `KeplerMatchRanking_${round}_${matchId}.csv` : `KeplerMatchRanking_${matchId}.csv`);
        });
      }
    });
  }
}
